"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";

type FormatNum = (value: number, options?: Intl.NumberFormatOptions) => string;

type Mode = "words-to-pages" | "pages-to-words";
type Font = "times-new-roman" | "arial" | "calibri" | "verdana";
type FontSize = 10 | 11 | 12 | 13 | 14;
type Spacing = "single" | "1.5" | "double";

/** 12pt Times New Roman, single-spaced, 1-inch margins on 8.5x11 ≈ 500 words/page. */
const BASE_WORDS_PER_PAGE_SINGLE = 500;

const FONTS: { value: Font; label: string; multiplier: number }[] = [
  { value: "times-new-roman", label: "Times New Roman", multiplier: 1 },
  { value: "arial", label: "Arial", multiplier: 0.94 },
  { value: "calibri", label: "Calibri", multiplier: 1.02 },
  { value: "verdana", label: "Verdana", multiplier: 0.82 },
];

const FONT_SIZES: FontSize[] = [10, 11, 12, 13, 14];

const SIZE_MULTIPLIERS: Record<FontSize, number> = {
  10: 1.32,
  11: 1.15,
  12: 1,
  13: 0.88,
  14: 0.78,
};

type Strings = {
  [K in keyof typeof WORDS_PER_PAGE_CALCULATOR_STRINGS]: string;
};

const SPACINGS: { value: Spacing; label: keyof Strings; multiplier: number }[] =
  [
    { value: "single", label: "spacingSingle", multiplier: 1 },
    { value: "1.5", label: "spacingOneAndHalf", multiplier: 1 / 1.5 },
    { value: "double", label: "spacingDouble", multiplier: 0.5 },
  ];

const REFERENCE_WORD_COUNTS = [500, 1000, 1500, 2000, 2500];

function fontMultiplier(font: Font): number {
  return FONTS.find((f) => f.value === font)?.multiplier ?? 1;
}

function spacingMultiplier(spacing: Spacing): number {
  return SPACINGS.find((s) => s.value === spacing)?.multiplier ?? 1;
}

function wordsPerPage(font: Font, size: FontSize, spacing: Spacing): number {
  return (
    BASE_WORDS_PER_PAGE_SINGLE *
    fontMultiplier(font) *
    SIZE_MULTIPLIERS[size] *
    spacingMultiplier(spacing)
  );
}

function formatPages(pages: number, formatNum: FormatNum): string {
  const rounded = Math.round(pages * 10) / 10;
  return formatNum(rounded, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function formatWords(words: number, formatNum: FormatNum): string {
  return formatNum(Math.round(words));
}

/**
 * One field, two units — and only one of them is whole.
 *
 * The mode toggle changes what the reader is typing: a word count going one way, a page
 * count going the other. Word counts are integers and arrive with grouping separators,
 * because "1,000" and "1.000" are how people write a thousand. Page counts are not
 * integers — "how many words is 2.5 pages" is the question the tool exists to answer,
 * and half a page is the most ordinary fraction there is.
 *
 * A single `parseIntegerInput` here read the word-count case correctly and quietly
 * returned 0 for a page-and-a-half, so the parse has to know which unit it is reading.
 */
function parseCount(raw: string, mode: Mode): number {
  const n =
    mode === "words-to-pages" ? parseIntegerInput(raw) : parseNumberInput(raw);
  if (n === null || n < 0) {
    return 0;
  }
  return n;
}

function OptionGroup<T extends string>({
  label,
  name,
  options,
  selected,
  onSelect,
}: {
  label: string;
  name: string;
  options: { value: T; label: string }[];
  selected: T;
  onSelect: (value: T) => void;
}) {
  return (
    <section className="space-y-3">
      <p className="font-medium text-foreground text-sm">{label}</p>
      <div className="flex flex-wrap gap-3">
        {options.map((option) => (
          <label
            className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
            key={option.value}
          >
            <input
              checked={selected === option.value}
              className="accent-primary"
              name={name}
              onChange={() => onSelect(option.value)}
              type="radio"
              value={option.value}
            />
            {option.label}
          </label>
        ))}
      </div>
    </section>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

function resultLabel(mode: Mode, strings: Strings): string {
  return mode === "words-to-pages"
    ? strings.estimatedPages
    : strings.estimatedWords;
}

function resultValue(
  mode: Mode,
  count: number,
  wpp: number,
  formatNum: FormatNum
): string {
  if (mode === "words-to-pages") {
    return formatPages(count / wpp, formatNum);
  }
  return formatWords(count * wpp, formatNum);
}

/** English strings. A translated content file overrides these through `ui`. */
export const WORDS_PER_PAGE_CALCULATOR_STRINGS = {
  conversion: "Conversion",
  font: "Font",
  fontSize: "Font size",
  pt: "pt",
  lineSpacing: "Line spacing",
  wordsPerPageAtThese: "Words per page at these settings",
  quickReferenceAtYourSettings: "Quick reference at your settings",
  words: "Words",
  estimatedPages: "Estimated pages",
  estimatesAssumeAStandard8:
    "Estimates assume a standard 8.5 × 11 inch page with 1-inch margins and continuous body text. Headings, block quotes, images, and paragraph breaks will change the real page count.",
  estimatedWords: "Estimated words",
  wordCount: "Word count",
  numberOfPages: "Number of pages",
  modeWordsToPages: "Words → pages",
  modePagesToWords: "Pages → words",
  spacingSingle: "Single",
  spacingOneAndHalf: "1.5 lines",
  spacingDouble: "Double",
} as const;

export default function WordsPerPageCalculatorWidget() {
  const [mode, setMode] = useState<Mode>("words-to-pages");
  const [countInput, setCountInput] = useState("1000");
  const [font, setFont] = useState<Font>("times-new-roman");
  const [size, setSize] = useState<FontSize>(12);
  const [spacing, setSpacing] = useState<Spacing>("double");

  const count = parseCount(countInput, mode);
  const wpp = wordsPerPage(font, size, spacing);
  const hasInput = count > 0;
  const strings = useToolUi(WORDS_PER_PAGE_CALCULATOR_STRINGS);
  const inputLabel =
    mode === "words-to-pages" ? strings.wordCount : strings.numberOfPages;

  const formatNum = useToolNumber();

  return (
    <>
      {/* Mode */}
      <OptionGroup
        label={strings.conversion}
        name="wpp-mode"
        onSelect={setMode}
        options={[
          { value: "words-to-pages", label: strings.modeWordsToPages },
          { value: "pages-to-words", label: strings.modePagesToWords },
        ]}
        selected={mode}
      />
      {/* Count input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="wpp-count"
        >
          {inputLabel}
        </label>
        <input
          className="w-full max-w-xs rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          id="wpp-count"
          inputMode="decimal"
          min={0}
          onChange={(e) => setCountInput(e.target.value)}
          type="number"
          value={countInput}
        />
      </div>
      {/* Font */}
      <OptionGroup
        label={strings.font}
        name="wpp-font"
        onSelect={setFont}
        options={FONTS.map((f) => ({ value: f.value, label: f.label }))}
        selected={font}
      />
      {/* Font size */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.fontSize}
        </p>
        <div className="flex flex-wrap gap-3">
          {FONT_SIZES.map((option) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={option}
            >
              <input
                checked={size === option}
                className="accent-primary"
                name="wpp-size"
                onChange={() => setSize(option)}
                type="radio"
                value={option}
              />
              {option}
              {strings.pt}
            </label>
          ))}
        </div>
      </section>
      {/* Spacing */}
      <OptionGroup
        label={strings.lineSpacing}
        name="wpp-spacing"
        onSelect={setSpacing}
        options={SPACINGS.map((s) => ({
          value: s.value,
          label: strings[s.label],
        }))}
        selected={spacing}
      />
      {/* Result */}
      {hasInput ? (
        <div aria-live="polite" className="grid grid-cols-2 gap-4">
          <StatCard
            label={resultLabel(mode, strings)}
            value={resultValue(mode, count, wpp, formatNum)}
          />
          <StatCard
            label={strings.wordsPerPageAtThese}
            value={formatWords(wpp, formatNum)}
          />
        </div>
      ) : null}
      {/* Quick reference */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.quickReferenceAtYourSettings}
        </h2>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50 text-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">{strings.words}</th>
                <th className="px-4 py-3 font-medium">
                  {strings.estimatedPages}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-muted-foreground">
              {REFERENCE_WORD_COUNTS.map((words) => (
                <tr key={words}>
                  <td className="px-4 py-3">{formatWords(words, formatNum)}</td>
                  <td className="px-4 py-3">
                    {formatPages(words / wpp, formatNum)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.estimatesAssumeAStandard8}
        </p>
      </section>
    </>
  );
}
